import React from 'react';
import Badge from '../../../ui/Badge';
import { formatMoney } from '../../../constants/finance';

// Shared bits of the deposit reports (day-13 collections, month report, refunds):
// formatting, the deposit-kind chips, the terminal label and the striped HTML table
// the staff paste into an email.
// the one money format (DUP-27): 2 decimals, en-AE grouping, AED first, empty -> — (the email columns are headed AED)
export const money = (v) => formatMoney(v, { currency: false });

// [label (also used in the email tables), Badge variant]
export const KIND = {
    block: ['CC block', 'warning'], purchase: ['CC purchase', 'info'],
    cash: ['Cash deposit', 'success'], none: ['NO deposit', 'danger'],
    linked: ['Block on other contract', 'accent'], unknown: ['No deposit comment', 'neutral'],
    // staff wording (27 Sep 2026): "Credit Card" alone - block or charge not written; money cleared from a deposit
    card: ['Credit card', 'warning'], taken: ['Deposit (type not written)', 'neutral'],
};

/**
 * The "Collected from the deposit" form for a day-13 row, pre-filled with the amount to collect -
 * null when there is nothing to mark (an OPEN contract renews its block; a done row owes nothing).
 * Staff could not open the form before: nothing set it (audit MON-13).
 */
export const markCollectedFor = (r) => {
    if (!r || !r.contract || r.status === 'open' || r.status === 'done') return null;
    const due = Number(r.toCollect);
    // collected in full, Ejar just does not show it yet: nothing more to mark (owner, 19 Oct 2026)
    if (r.status === 'collected_not_on_ejar' && !(due > 0.05)) return null;
    return { contract: r.contract, amount: due > 0 ? due.toFixed(2) : '', note: '' };
};

/**
 * The day-13 list split by what the airport staff must DO (owner, 19 Oct 2026: one table mixing every row was
 * not clear). Each row is in exactly one group; the order is the order of the tiles.
 */
export const DAY13_GROUPS = [
    { id: 'collect', label: 'To collect', hint: 'Ejar still shows money owed - charge the card / deposit', tone: 'text-red-700', has: (r) => r.status === 'needs_collection' },
    { id: 'register', label: 'Register on Ejar first', hint: 'fines / Salik / parking not on Ejar - register them, then collect', tone: 'text-amber-700', has: (r) => r.status === 'needs_registration' },
    { id: 'onEjar', label: 'Collected - put on Ejar', hint: 'marked collected but Ejar does not show the payment yet', tone: 'text-blue-700', has: (r) => r.status === 'collected_not_on_ejar' },
    { id: 'check', label: 'Check by hand', hint: 'Ejar could not be read for these', tone: 'text-amber-700', has: (r) => r.status === 'check_failed' },
    { id: 'open', label: 'Still open', hint: 'contract still open - renew the card block before it expires', tone: 'text-purple-700', has: (r) => r.status === 'open' },
    { id: 'done', label: 'Done', hint: 'nothing owed', tone: 'text-green-700', has: (r) => r.status === 'done' },
];
export const groupOf = (r) => (DAY13_GROUPS.find((g) => g.has(r)) || { id: 'check' }).id;

/**
 * Charges staff took off Ejar on purpose (the engine's "removed on Ejar"): never written back automatically, so the page
 * says why they still show as "not on Ejar" (owner, 19 Oct 2026). -> {n, amount} or null.
 */
export const removedOnEjar = (r) => {
    const parts = ['toll', 'darb', 'parking'].map((k) => r?.unregistered?.[k]?.removedOnEjar).filter(Boolean);
    if (!parts.length) return null;
    return { n: parts.reduce((a, x) => a + (x.n || 0), 0), amount: Math.round(parts.reduce((a, x) => a + (x.amount || 0), 0) * 100) / 100 };
};

/** The deposit filter's choices (owner, 19 Oct 2026: a CC blocking filter): [value, label]. */
export const DEPOSIT_FILTERS = [
    ['all', 'All deposits'], ['block', 'CC block'], ['purchase', 'CC purchase'], ['cash', 'Cash deposit'],
    ['card', 'Credit card (block or charge not written)'], ['taken', 'Deposit (type not written)'],
    ['linked', 'Block on other contract'], ['none', 'No deposit'],
];

/** Day / branch / deposit / search filters of the day-13 list (applied before the group tiles count). */
export const day13Matches = (r, { stage = 'all', branch = 'all', deposit = 'all', q = '' } = {}) => {
    if (stage !== 'all' && r.stage !== stage) return false;
    if (branch !== 'all' && terminal(r) !== branch) return false;
    const kind = r.deposit?.kind || 'unknown';
    // "No deposit" = none written or no deposit comment; a monthly contract has no deposit rule (owner, 1 Oct 2026)
    if (deposit === 'none' && !(['none', 'unknown'].includes(kind) && !r.monthly)) return false;
    if (deposit !== 'all' && deposit !== 'none' && kind !== deposit) return false;
    const needle = String(q || '').trim().toLowerCase();
    if (!needle) return true;
    return [r.contract, r.customer, r.plate].some((v) => String(v || '').toLowerCase().includes(needle));
};

const STAGE_FILTERS = [['all', 'All days'], ['today', 'Last day today'], ['tomorrow', 'Last day tomorrow'], ['missed', 'Missed (day 14)']];

/**
 * A filter's choices built from the page (owner, 19 Oct 2026: "show only what is on the page"): every value with at
 * least one row once the OTHER filters are applied, with its count - plus "All" and the value already chosen.
 * ``field`` = stage | branch | deposit. -> [{value, label}]
 */
export const day13FilterOptions = (rows, filters, field) => {
    const list = rows || [];
    const catalogue = field === 'stage' ? STAGE_FILTERS : field === 'deposit' ? DEPOSIT_FILTERS
        : [['all', 'All'], ...[...new Set(list.map(terminal))].sort().map((t) => [t, t])];
    const count = (value) => list.filter((r) => day13Matches(r, { ...filters, [field]: value })).length;
    return catalogue
        .map(([value, label]) => ({ value, label, n: count(value) }))
        .filter((o) => o.value === 'all' || o.n > 0 || o.value === filters?.[field])
        .map((o) => ({ value: o.value, label: `${o.label} (${o.n})` }));
};

// -- the month report's filters (owner, 20 Oct 2026: "مفيش فيه فلترة") ----------------------------------------------
export const MONTH_NO_FILTERS = { branch: 'all', deposit: 'all', status: 'all', q: '', from: '', to: '' };
export const MONTH_STATUS = [['all', 'All statuses'], ['needs_collection', 'Needs collection'], ['needs_registration', 'Needs registration on Ejar'],
    ['collected_not_on_ejar', 'Collected - not on Ejar yet'], ['check_failed', 'Ejar could not be read'], ['open', 'Still open'], ['done', 'Done']];

/** A month row against the month filters: branch / deposit / search as the day-13 list, its status, its "listed on" day. */
export const monthMatches = (r, f = MONTH_NO_FILTERS) => {
    if (!day13Matches(r, { branch: f.branch, deposit: f.deposit, q: f.q })) return false;
    if (f.status && f.status !== 'all' && r.status !== f.status) return false;
    const day = String(r.listedOn || '');
    if (f.from && day < f.from) return false;
    if (f.to && day > f.to) return false;
    return true;
};

/** A month filter's choices with their counts (the other filters applied): field = branch | deposit | status. */
export const monthFilterOptions = (rows, filters, field) => {
    const list = rows || [];
    const catalogue = field === 'status' ? MONTH_STATUS : field === 'deposit' ? DEPOSIT_FILTERS
        : [['all', 'All'], ...[...new Set(list.map(terminal))].sort().map((t) => [t, t])];
    const count = (value) => list.filter((r) => monthMatches(r, { ...filters, [field]: value })).length;
    return catalogue
        .map(([value, label]) => ({ value, label, n: count(value) }))
        .filter((o) => o.value === 'all' || o.n > 0 || o.value === filters?.[field])
        .map((o) => ({ value: o.value, label: `${o.label} (${o.n})` }));
};

/** The month tiles' figures for the rows shown (the engine's summary is the same sums over every row). */
export const monthSummary = (list) => {
    const sum = (k) => Math.round((list || []).reduce((a, r) => a + (Number(r[k]) || 0), 0) * 100) / 100;
    return {
        contracts: (list || []).length, toCollect: sum('toCollect'), collected: sum('collected'), outstanding: sum('outstanding'),
        done: (list || []).filter((r) => r.status === 'done').length, open: (list || []).filter((r) => r.status === 'open').length,
        noDeposit: (list || []).filter((r) => ['none', 'unknown'].includes(r.deposit?.kind || 'unknown') && !r.monthly).length,
    };
};

/** "Dubai Airport Terminal 1" -> "T1" (the branch column stays narrow). */
export const terminal = (r) => { const b = String(r.branch || ''); const m = b.match(/terminal\s*(\d)/i); return m ? `T${m[1]}` : (b || '—'); };

/** Terminal chip (T1 / T2). */
export function TerminalBadge({ row }) {
    const t = terminal(row);
    return <Badge variant={t === 'T2' ? 'info' : 'accent'} size="sm">{t}</Badge>;
}

/** Inline message line: tone success | danger | neutral. */
export function Notice({ tone = 'neutral', children }) {
    const cls = tone === 'success'
        ? 'bg-green-50 border-green-200 text-green-800'
        : tone === 'danger'
            ? 'bg-red-50 border-red-200 text-red-800'
            : tone === 'warning'
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-gray-50 border-gray-200 text-gray-700';
    return <div role={tone === 'danger' ? 'alert' : 'status'} className={`text-sm border rounded-lg px-3 py-2 ${cls}`}>{children}</div>;
}

// Striped HTML table for pasting into an email. Header = brand yellow with black text
// (Outlook drops the font colour on dark headers), <td> cells with bgcolor + inline
// styles. `cols` = [{h, get(row, i), bold, red}], `total` = {label, cells: {colIndex: text}}.
// Every cell is centred - header, body and TOTAL, money and notes alike (owner rule: every table
// cell centred; the copied tables used to right-align money and left-align notes).
export const htmlTable = (cols, list, total) => {
    const base = 'padding:8px;border:1px solid #d9d2ee;font-family:Arial,sans-serif;font-size:13px;text-align:center;';
    const head = cols.map((c) => `<td bgcolor="#FFD600" align="center" style="${base}background:#FFD600;background-color:#FFD600;font-weight:bold;color:#000000;"><b>${c.h}</b></td>`).join('');
    const body = list.map((r, i) => {
        const bg = i % 2 ? '#f3f0fa' : '#ffffff';
        return `<tr bgcolor="${bg}">${cols.map((c) => {
            const v = c.get(r, i);
            const st = `${base}background-color:${bg};${c.bold ? 'font-weight:bold;' : ''}${c.red ? 'color:#c62828;' : ''}`;
            return `<td align="center" style="${st}">${v == null ? '' : v}</td>`;
        }).join('')}</tr>`;
    }).join('');
    const tot = total ? `<tr bgcolor="#ede9fe">${cols.map((c, i) => {
        const v = total.cells[i];
        const st = `${base}background-color:#ede9fe;font-weight:bold;${c.red ? 'color:#c62828;' : ''}`;
        return `<td align="center" style="${st}">${i === 0 ? total.label : (v == null ? '' : v)}</td>`;
    }).join('')}</tr>` : '';
    return `<table cellpadding="0" cellspacing="0" border="1" bordercolor="#d9d2ee" style="border-collapse:collapse;width:100%;">
<thead><tr bgcolor="#FFD600">${head}</tr></thead><tbody>${body}${tot}</tbody></table>`;
};

/** Copy HTML (+ plain-text fallback) to the clipboard; resolves to the message to show. */
export const copyHtml = async (html, plain, okMsg) => {
    try {
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([plain], { type: 'text/plain' }) })]);
        return okMsg;
    } catch {
        try { await navigator.clipboard.writeText(plain); return 'Plain text copied - paste it into the email draft.'; } catch { return 'Could not copy - allow clipboard access.'; }
    }
};

/** A summary tile; clickable (filter) when `id` and `onPick` are given. */
export function Tile({ label, value, tone = '', id, hint, active, onPick }) {
    const clickable = !!id && !!onPick;
    const body = (
        <>
            <span className="block text-xs font-medium text-gray-500">{label}</span>
            <span className={`block text-xl font-semibold tabular-nums ${tone || 'text-gray-900'}`}>{value}</span>
        </>
    );
    const base = 'text-left rounded-lg border bg-white px-4 py-3 min-w-[150px]';
    if (!clickable) return <div className={`${base} border-gray-200`}>{body}</div>;
    return (
        <button
            type="button"
            onClick={() => onPick(id)}
            aria-pressed={!!active}
            title={active ? 'Click to show all' : `Show only: ${hint || label}`}
            className={`${base} transition-colors ${active ? 'border-purple-600 ring-1 ring-purple-600 bg-purple-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
        >
            {body}
        </button>
    );
}
